import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { BannerFormState, BannerTranslationState } from './BannerFormState'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSingleBanner } from '@@partner:graphql/query/banners/singleBanner.gql'
import { useMutateSaveBanner } from '@@partner:graphql/mutation/banners/saveBanner.gql'
import { ensureUrlProtocol } from '@@partner:helpers/util'
import { useFetchAllBannerTypes } from '@@partner:graphql/query/banners/allBannerTypes.gql'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  BANNER_TRANSLATION_DEFAULT_STATE,
  makeBannerFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './BannerFormState'

import {
  Form,
  Grid,
  Toggle,
  Panel,
  Row,
  Col,
  InputGroup,
  DatePicker,
  SelectPicker
} from 'rsuite'
import DropzonePreview from '@components/common/form/DropzonePreview'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import FormElementErrorDisplay from '@components/common/form/FormElementErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import FormSelect from '@components/common/form/FormSelect'

import { BANNER } from '@hive/status'
import {
  API_URL_UPLOAD_BANNER_IMAGE,
  API_URL_UPLOAD_MOBILE_BANNER_IMAGE
} from '@@partner:config/api'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import ColorPickerField from '@components/common/commonFormInput/ColorPickerField'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'
import ObjectLinkSelector from '@components/common/ObjectLinkSelector'

export interface BannerFormProps extends CrudFormProps {
  bannerId?: GQL_ID
}

export default function BannerForm ({ bannerId, onSaved }: BannerFormProps) {
  const [ formError, setFormError ] = useState({})
  const { formState, setFormState, setFormRef } =
    useValidatableForm<BannerFormState>(makeBannerFormState)
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>('По-умолчанию')

  const {
    data,
    loading: loadingBanner,
    error: errorLoadingBanner
  } = useFetchSingleBanner({
    id: bannerId,
    desktopStyle: '1200xauto',
    mobileStyle: '800xauto'
  })

  const { data: allBannerTypesData, loading: loadingBannerTypes } =
    useFetchAllBannerTypes({
      pager: {
        limit: 10
      }
    })

  const selectedData = selectedLanguage === 'По-умолчанию'
    ? {
      selectedLanguage: 'По-умолчанию',
      file_id: formState.file_id,
      mobile_file_id: formState.mobile_file_id,
      bannerSlide: formState.bannerSlide,
      bannerMobileSlide: formState.bannerMobileSlide,
      button_text: formState.button_text,
    }
    : formState.i18n.find((item) => {
      return item.language_code === selectedLanguage
    })

  const allBannerTypes = useMemo(
    () => {
      const prepared = (allBannerTypesData?.allBannerTypes?.edges ?? []).map((item: any) => ({
        label: item.label,
        value: Number(item.id)
      }))

      return [
        ...prepared,
        {
          label: 'Не выбрано',
          value: undefined
        }
      ]
    },
    [ allBannerTypesData ]
  )

  const [ saveBanner, { loading: loadingSaveBanner } ] = useMutateSaveBanner()

  useEffect(() => {
    if (!loadingBanner && data) {
      console.log(data)
      setFormState((state) => mergeLeft(state, mapApiDataToState(data?.banner)))
    }
  }, [ data, loadingBanner, setFormState ])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleTranslateChange = useCallback(
    (field: keyof BannerTranslationState) => (value: string) => {
      if (selectedLanguage === 'По-умолчанию') {
        setFormState((state) => mergeLeft(state, { [field]: value }))
        return
      }
      else {
        const newTranslations = [ ...formState.i18n ]
        const translationIndex = newTranslations.findIndex(translation => translation.language_code === selectedLanguage)
        if (translationIndex > -1) {
          newTranslations[translationIndex][field] = value
        }
        else {
          const newTranslation = mergeLeft(BANNER_TRANSLATION_DEFAULT_STATE, {
            language_code: selectedLanguage,
            [field]: value
          })
          newTranslations.push(newTranslation)
        }
        setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
      }
    },
    [ formState.i18n, selectedLanguage, setFormState ]
  )

  const handleBannerUploaded = useCallback(
    (file_id: GQL_ID, url: string) => {
      setFormState((state) => {
        if (selectedLanguage === 'По-умолчанию') {
          return {
            ...state,
            file_id,
            bannerSlide: { url }
          }
        }
        else {
          const newTranslations = [ ...state.i18n ]
          const translationIndex = newTranslations.findIndex(translation => translation.language_code === selectedLanguage)
          if (translationIndex > -1) {
            newTranslations[translationIndex].bannerSlide = { url }
            newTranslations[translationIndex].file_id = file_id
          }
          else {
            const newTranslation = mergeLeft(
              BANNER_TRANSLATION_DEFAULT_STATE, 
              {
                language_code: selectedLanguage,
                bannerSlide: { url },
                file_id: file_id
              })
            newTranslations.push(newTranslation)
          }
          return {
            ...state,
            i18n: newTranslations
          }
        }
      })
    },
    [ setFormState, selectedLanguage ]
  )

  const handleMobileBannerUploaded = useCallback(
    (mobile_file_id: GQL_ID, url: string) => {
      setFormState((state) => {
        if (selectedLanguage === 'По-умолчанию') {
          return {
            ...state,
            mobile_file_id,
            bannerMobileSlide: { url }
          }
        }
        else {
          const newTranslations = [ ...state.i18n ]
          const translationIndex = newTranslations.findIndex(translation => translation.language_code === selectedLanguage)
          if (translationIndex > -1) {
            newTranslations[translationIndex].bannerMobileSlide = { url }
            newTranslations[translationIndex].mobile_file_id = mobile_file_id
          }
          else {
            const newTranslation = mergeLeft(
              BANNER_TRANSLATION_DEFAULT_STATE, 
              {
                language_code: selectedLanguage,
                bannerMobileSlide: { url },
                mobile_file_id
              })
            newTranslations.push(newTranslation)
          }
          return {
            ...state,
            i18n: newTranslations
          }
        }
      })
    },
    [ setFormState, selectedLanguage ]
  )

  const handleUnlinkBannerClick = useCallback(() => {
    setFormState((state) =>
      mergeLeftOnly(
        [ 'file_id', 'bannerSlide', 'url' ],
        state,
        makeBannerFormState()
      )
    )
  }, [ setFormState ])

  const handleUnlinkBannerMobileClick = useCallback(() => {
    setFormState((state) =>
      mergeLeftOnly(
        [ 'mobile_file_id', 'bannerMobileSlide', 'url' ],
        state,
        makeBannerFormState()
      )
    )
  }, [ setFormState ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (formValue: Record<string, any> | null) => {
      if (formValue) {
        saveBanner({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: formState.id ? false : true
          }
        })
          .then(({ data }) => {
            notifyOk('Баннер сохранён')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.bannerInfo)
            )

            onSaved?.(data?.bannerInfo.file_id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveBanner, formState, setFormState, onSaved ]
  )

  if (errorLoadingBanner) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить баннер #${bannerId} для редактирования`}
        error={errorLoadingBanner}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={setFormRef}
      fluid
    >
      <Grid fluid>
        <Row>
          <Col xs={24} sm={24} md={24} lg={12} xl={8}>
            <Panel 
              header="Основные данные о товаре"
              shaded
              className='my-5'
            >
              <Row className='my-5'>
                <Form.Group>
                  <Form.ControlLabel>Название</Form.ControlLabel>
                  <Form.Control name="name" placeholder="Название" />
                </Form.Group>
              </Row>
              <Row className='my-5'>
                <Col xs={24} sm={12} className='my-5'>
                  <Form.Group>
                    <Form.ControlLabel>Активен</Form.ControlLabel>
  
                    <Form.Control
                      accepter={Toggle}
                      size="lg"
                      name="status"
                      checkedChildren="АКТИВЕН"
                      unCheckedChildren="СКРЫТ"
                      checked={formState.status === BANNER.ENABLED ? true : false}
                      value={formState.status ? 'true' : 'false'}
                      onChange={(value) =>
                        setFormState((state) => ({
                          ...state,
                          status: value === true ? 1 : 0
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
                
                <Col xs={24} sm={12} className='my-5'>
                  <Form.Group>
                    <Form.ControlLabel>Тип баннера</Form.ControlLabel>
                    <FormSelect
                      name="selectedBannerTypeId"
                      data={allBannerTypes}
                    />
                  </Form.Group>
                </Col>
                <Col xs={24} className='my-5'>
                  <Form.ControlLabel>Период действия</Form.ControlLabel>
                  <InputGroup>
                    <Form.Control
                      accepter={DatePicker}
                      block
                      oneTap
                      isoWeek
                      appearance="subtle"
                      name="activeFrom"
                      value={formState.activeFrom}
                      format="yyyy-MM-dd HH:mm:ss"
                      placeholder="дата начала действия"
                    />
  
                    <InputGroup.Addon> до </InputGroup.Addon>
  
                    <Form.Control
                      accepter={DatePicker}
                      block
                      oneTap
                      isoWeek
                      appearance="subtle"
                      name="activeUntil"
                      value={formState.activeUntil}
                      format="yyyy-MM-dd HH:mm:ss"
                      placeholder="дата окончания действия"
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Panel>
            <Panel 
              header="Кнопка"
              shaded
              className='my-5'
            >
              <Row className='my-5'>
                <Form.Group>
                  <Form.ControlLabel>Ссылка</Form.ControlLabel>
                  <Form.Control
                    name="url"
                    placeholder="Ссылка"
                    onBlur={(event) =>
                      setFormState((state: any) => ({
                        ...state,
                        url: ensureUrlProtocol(event.target.value)
                      }))
                    }
                  />
                </Form.Group>
                <ObjectLinkSelector 
                  onSelect={(url) => setFormState((state) => ({ ...state, url }))}
                />
                <Col xs={24} sm={12} className='my-5'>
                  <Form.Group>
                    <Form.ControlLabel>Открыть в новом окне?</Form.ControlLabel>
                    <Form.Control
                      accepter={Toggle}
                      size="lg"
                      name="optionOpenInNewWindow"
                      checkedChildren="ВКЛЮЧЕНО"
                      unCheckedChildren="ВЫКЛЮЧЕНО"
                      checked={!!formState.optionOpenInNewWindow}
                      value={!formState.optionOpenInNewWindow ? 'false' : 'true'}
                    />
                  </Form.Group>
                </Col>
                <Col xs={24} sm={12} className='my-5'>
                  <Form.Group>
                    <Form.ControlLabel>
                      Открыть в браузере (приложение)?
                    </Form.ControlLabel>
                    <Form.Control
                      accepter={Toggle}
                      size="lg"
                      name="optionOpenInBrowser"
                      checkedChildren="ВКЛЮЧЕНО"
                      unCheckedChildren="ВЫКЛЮЧЕНО"
                      checked={!!formState.optionOpenInBrowser}
                      value={!formState.optionOpenInBrowser ? 'false' : 'true'}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className='my-5'>
                <Col xs={8} sm={12} className='my-5'>
                  <Form.Group>
                    <Form.ControlLabel>Есть кнопка</Form.ControlLabel>
                    <Form.Control
                      accepter={Toggle}
                      size="lg"
                      name="has_button"
                      checkedChildren="Да"
                      unCheckedChildren="Нет"
                      checked={!!formState.has_button}
                      value={!formState.has_button ? 'false' : 'true'}
                      onChange={(has_button) =>
                        setFormState((state) => ({
                          ...state,
                          has_button
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
                { formState.has_button && (
                  <Col xs={24}>
                    <SelectLocale
                      selectedLanguage={selectedLanguage}
                      onSelect={(language) => setSelectedLanguage(language)}
                      onAdd={(language) => {
                        setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
                      }}
                      languages={formState.languages}
                    />
                    <Row className='my-5'>
                      <LocalizedForm
                        name='button_text'
                        label='Текст кнопки'
                        selectedLanguage={selectedLanguage}
                        value={selectedData?.button_text ?? ''}
                        onChange={handleTranslateChange('button_text')}
                      />
                    </Row>
                  </Col>
                )}
                { formState.has_button && (<Col xs={24}>
                  <ColorPickerField  
                    label='Цвет кнопки'
                    value={formState.button_color ?? ''}
                    onChange={(button_color) => {
                      setFormState((state) => ({
                        ...state,
                        button_color
                      }))
                    }}
                  />
                </Col>
                )}
              </Row>
            </Panel>

          </Col>
          <Col
            xs={24}
            lg={12}
            xl={16}
            className="py-4 px-2 text-lg"
          >
            <SelectLocale
              selectedLanguage={selectedLanguage}
              onSelect={(language) => setSelectedLanguage(language)}
              onAdd={(language) => {
                setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
              }}
              languages={formState.languages}
            />
            <Panel 
              shaded 
              header='Баннер для ПК-версии'
              className='my-5'
            >
              {!selectedData?.bannerSlide?.url ? (
                <Form.Group>
                  <DropzoneUploader
                    action={API_URL_UPLOAD_BANNER_IMAGE}
                    name="banner-slide-image"
                    onUploaded={handleBannerUploaded}
                    multiple={false}
                    imageStyle={'1200xauto'}
                  />
                </Form.Group>
              ) : (
                <Form.Group>
                  {selectedData.bannerSlide?.url && (
                    <DropzonePreview
                      url={selectedData.bannerSlide.url}
                      onDelete={handleUnlinkBannerClick}
                      imageClassName="w-full"
                    />
                  )}
                </Form.Group>
              )}
            </Panel>
            <Panel 
              shaded 
              header='Баннер для мобильной версии'
              className='my-5 py-5'
            >
              { !selectedData?.bannerMobileSlide?.url ? (
                <Form.Group>
                  <DropzoneUploader
                    action={API_URL_UPLOAD_MOBILE_BANNER_IMAGE}
                    name="banner-mobile-slide-image"
                    onUploaded={handleMobileBannerUploaded}
                    multiple={false}
                    imageStyle={'1200xauto'}
                  />
                </Form.Group>
              ) : (
                <Form.Group>
                  {selectedData.bannerMobileSlide?.url && (
                    <DropzonePreview
                      url={selectedData.bannerMobileSlide.url}
                      onDelete={handleUnlinkBannerMobileClick}
                      imageClassName="w-full"
                    />
                  )}
                </Form.Group>
              )}
            </Panel>
          </Col>
        </Row>
      </Grid>
      <SaveButton
        loading={loadingBanner || loadingSaveBanner}
        disabled={loadingBanner || loadingSaveBanner}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
